import { Field, Formik, useFormikContext } from 'formik'
import { object, string } from 'yup'
import { AnimatePresence, AnimateSharedLayout } from 'framer-motion'
import React from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { setAccountType, stepForward } from '../../store/reducers/slices/checkout'
import { login } from '../../store/thunks/account/login'
import { register } from '../../store/thunks/account/register'
import { guestCheckout } from '../../store/thunks/checkout/guest-checkout'
import FormLabel from '../form/form-label'
import FormRadioOption from '../form/form-radio-option'
import Link from '../link'
import { withNullErrorBoundary } from '../../utils'
import { motion } from '../motion'
import { passwordReset } from '../user-accounts/pages/routes'

const FormButton = (props) => <button className="btn btn-outline-primary collapsed continue-to-payment" type="submit" {...props} />

const FormWrapper = ({ children, ...props }) => {
  const { handleReset, handleSubmit } = useFormikContext()

  return (
    <motion.form
      onSubmit={handleSubmit}
      onReset={handleReset}
      { ...props }
      variants={{
        show: { opacity: 1 },
        hide: { opacity: 0 },
      }}
      initial="hide"
      animate="show"
      exit="hide"
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.form>
  )
}

export const ErrorMessage = ({ name }) => {
  const { errors, touched } = useFormikContext()
  const error = errors[name]
  const hasTouched = touched[name]

  return (
    <AnimatePresence>
      {hasTouched && error && (
        <motion.div
          className="form-error__wrapper"
          variants={{
            show: { height: 'auto', opacity: 1 },
            hide: { height: 0, opacity: 0 },
          }}
          initial="hide"
          animate="show"
          exit="hide"
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
        >
          <div className="form-error__inner">
            {error}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const GuestFormSchema = object().shape({
  firstName: string().required('Required'),
  lastName: string().required('Required'),
  email: string().email('Email is invalid').required('Required'),
})

const GuestForm = () => {
  const pending = useSelector(state => state.checkout.pending.account)
  const initialValues = useSelector(state => state.checkout.data.checkout.customer)
  const dispatch = useDispatch()
  const onSubmit = (values, actions) => {
    dispatch(guestCheckout(values))
    actions.setSubmitting(false)
  }

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        ...initialValues,
      }}
      onSubmit={onSubmit}
      validationSchema={GuestFormSchema}
    >
      {({ isSubmitting, isValid, dirty, values }) => (
        <FormWrapper className="checkout-form-wrp guest col-12 show">
          <div className="form-group">
            <FormLabel htmlFor="firstName" title="First Name" className="required" />
            <ErrorMessage name="firstName" />
            <Field id="firstName" name="firstName" autoComplete="off" className="form-control" type="text" />
          </div>

          <div className="form-group">
            <FormLabel htmlFor="lastName" title="Last Name" className="required" />
            <ErrorMessage name="lastName" />
            <Field id="lastName" name="lastName" autoComplete="off" className="form-control" type="text" />
          </div>

          <div className="form-group">
            <FormLabel htmlFor="email" title="Email Address" className="required" />
            <ErrorMessage name="email" />
            <Field id="email" name="email" autoComplete="off" className="form-control" type="email" />
          </div>

          <FormButton disabled={!(!isSubmitting && isValid && (dirty || values.email) && !pending)}>
            Continue to delivery details
          </FormButton>
        </FormWrapper>
      )}
    </Formik>

  )
}

const RegisterFormSchema = object().shape({
  firstName: string().required('Required'),
  lastName: string().required('Required'),
  email: string().email('Email is invalid').required('Required'),
  password: string().min(8, 'Minimum length of 8').required('Required'),
})

const RegisterForm = () => {
  const pending = useSelector(state => state.checkout.pending.account)
  const dispatch = useDispatch()
  const onSubmit = (values, actions) => {
    dispatch(register(values))
    actions.setSubmitting(false)
  }

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={RegisterFormSchema}
    >
      {({ isSubmitting, isValid, dirty }) => (
        <FormWrapper className="checkout-form-wrp create col-12 show">
          <div className="form-group">
            <FormLabel htmlFor="firstName" title="First Name" className="required" />
            <ErrorMessage name="firstName" />
            <Field id="firstName" name="firstName" autoComplete="off" className="form-control" type="text" />
          </div>

          <div className="form-group">
            <FormLabel htmlFor="lastName" title="Last Name" className="required" />
            <ErrorMessage name="lastName" />
            <Field id="lastName" name="lastName" autoComplete="off" className="form-control" type="text" />
          </div>

          <div className="form-group">
            <FormLabel htmlFor="email" title="Email Address" className="required" />
            <ErrorMessage name="email" />
            <Field id="email" name="email" autoComplete="off" className="form-control" type="email" />
          </div>

          <div className="form-group">
            <FormLabel htmlFor="password" title="Password" className="required" />
            <ErrorMessage name="password" />
            <Field id="password" name="password" placeholder="Min 8 characters" autoComplete="off" className="form-control" type="password" />
          </div>

          <FormButton disabled={!(!isSubmitting && isValid && dirty && !pending)}>
            Continue to delivery details
          </FormButton>
        </FormWrapper>
      )}
    </Formik>
  )
}

const LoginFormSchema = object().shape({
  email: string().required('Required'),
  password: string().required('Required'),
})

const LoginForm = () => {
  const pending = useSelector(state => state.checkout.pending.account)
  const dispatch = useDispatch()
  const onSubmit = (values, actions) => {
    dispatch(login(values))
    actions.setSubmitting(false)
  }

  const initialValues = {
    email: '',
    password: '',
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={LoginFormSchema}
    >
      {({ isSubmitting, isValid, dirty }) => (
        <FormWrapper className="checkout-form-wrp log-in col-12 show">
          <div className="form-group">
            <FormLabel htmlFor="email" title="Email Address" className="required" />
            <ErrorMessage name="email" />
            <Field id="email" name="email" autoComplete="off" className="form-control" type="email" />
          </div>

          <div className="form-group">
            <FormLabel htmlFor="password" title="Password" className="required" />
            <ErrorMessage name="password" />
            <Field id="password" name="password" autoComplete="off" className="form-control" type="password" />
          </div>

          <div className="form-group d-flex reset-password-form-group">
            <FormButton disabled={!(!isSubmitting && isValid && dirty && !pending)}>
              Log in
            </FormButton>
            <Link href={passwordReset()} className="text-center">
              Reset password
            </Link>
          </div>
        </FormWrapper>
      )}
    </Formik>
  )
}

const SubmitButton = () => {
  return (
    <button className="btn btn-outline-primary collapsed continue-to-payment" type="button" />
  )
}

class CheckoutStepOne extends React.Component {
  state = {
    alert: null,
  }

  render() {
    const { user, nextStep, alert, pending } = this.props

    return (
      <div className={['card-body', 'account', 'loader', `loader--${pending ? 'loading' : 'loaded'}`].join(' ')}>
        <div className="row">
          <motion.div layout className="col-12">
            <AnimatePresence>
              {alert && (
                <motion.div key={'alert'} className={['alert', 'alert-danger'].join(' ')}>
                  {alert}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {!user.isLogged ? (
          <AccountForms />
        ) : (
          <>
            <p><strong>First name: </strong> {user.firstName}</p>
            <p><strong>Last name: </strong> {user.lastName}</p>
            <p><strong>Email: </strong> {user.email}</p>

            <div className="row">
              <div className="form-group col-12">
                <FormButton onClick={nextStep}>
                      Continue
                </FormButton>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }
}

const withAccountForm = () => {
  const accountType = useSelector(state => state.checkout.accountType)

  switch (accountType) {
  case 'guest':
    return GuestForm
  case 'register':
    return RegisterForm
  case 'login':
    return LoginForm
  }
}

const AccountTypeButton = (props) => {
  const dispatch = useDispatch()
  const isChecked = useSelector(state => state.checkout.accountType === props.value)

  const onChange = () => {
    dispatch(setAccountType(props.value))
  }

  return (
    <FormRadioOption checked={isChecked} onChange={onChange} className="form-check" { ...props } uniqueName={`checkout-${props.value}`} groupName="checkout-account-type" />
  )
}

const AccountForms = () => {
  const Form = withAccountForm()
  const accountType = useSelector(state => state.checkout.accountType)

  return (
    <>
      <motion.div layout className="row">
        <label className="label">Checkout as:</label>
        <div className="form-group col-12 in-line account-radio-section">
          <AccountTypeButton value="register" title="New Account" />
          <AccountTypeButton value="guest" title="Guest" />
          <AccountTypeButton value="login" title="Log in" />
        </div>
      </motion.div>

      <AnimateSharedLayout>
        <motion.div className="row">
          <AnimatePresence exitBeforeEnter initial={false}>
            <Form key={accountType}>
              <div className="form-group col-12">
                <SubmitButton alert={(alert) => this.setState({ alert })} />
              </div>
            </Form>
          </AnimatePresence>
        </motion.div>
      </AnimateSharedLayout>
    </>
  )
}

export default connect(
  state => ({
    user: state.user,
    alert: state.checkout.error,
    pending: state.checkout.pending.account,
  }),
  dispatch => ({
    nextStep: () => dispatch(stepForward()),
  })
)(withNullErrorBoundary(CheckoutStepOne))
